/**
 * Shared helpers for the location "Redeemable Access Control Token Types"
 * field, used by LocationCreate, LocationEdit and LocationClone.
 *
 * The column stays what it always was — a jsonb array of ticket-type NAMES —
 * because posapp matches gate scans against those names
 * (services/accessControl, tokenResolver.matchesAccepted). Only the way an
 * operator supplies them changes: picked from a list instead of typed.
 */
import { parseTicketModifiers, permutationTokenNames } from "../tokens/variantName";

type TicketTypeItem = { id: number; name: string; event_id: number; modifiers?: unknown; token_payload?: unknown };

/**
 * Everything the picker can offer, from a GET_TICKETING_ITEM_NAMES result:
 * online ticket types plus box office POS items in the "Ticket" category.
 */
export const ticketTypeItems = (data: any): TicketTypeItem[] => [
  ...(data?.items_ticketing ?? []),
  ...(data?.box_office_ticket_items ?? []),
];

/**
 * Read the submitted field back into an array of names.
 *
 * The picker submits JSON. One ticket name in production already contains a
 * comma, so the old `split(",")` corrupted it — JSON avoids that. The comma
 * path is kept as a fallback so a value saved by the previous form, or typed
 * by hand, still parses.
 */
export const parseRedeemableTokens = (raw: string | null): string[] | null => {
  if (raw == null) return null;
  const value = raw.trim();
  if (!value) return null;

  if (value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const names = parsed
          .map((entry) => String(entry ?? "").trim())
          .filter(Boolean);
        return names.length > 0 ? names : null;
      }
    } catch {
      // fall through to the comma path
    }
  }

  const names = value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  return names.length > 0 ? names : null;
};

/**
 * The token names a ticket type mints, read from items_ticketing.token_payload
 * (an object keyed by token name, e.g. a 2-Day ticket carrying "... - Day 1"
 * and "... - Day 2"). Those are the keys the gate sees in tokens_balance, so
 * the picker has to offer them next to the ticket name (RP-2549).
 */
export const tokenPayloadNames = (raw: unknown): string[] => {
  let payload: unknown = raw;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return [];
    }
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
  return Object.keys(payload as Record<string, unknown>)
    .map((name) => name.trim())
    .filter(Boolean);
};

/**
 * Options for the picker: the selected event's ticket types, the tokens those
 * tickets mint, plus anything the location already holds.
 *
 * Keeping the stored values matters. About half of what is on locations today
 * matches no current ticket item — renamed, deleted, or mistyped. If the picker
 * offered only live items, opening a location and saving it would silently drop
 * those entries and change which tickets its gate accepts.
 */
export const buildTokenTypeOptions = (
  items: { name: string; event_id: number; modifiers?: unknown; token_payload?: unknown }[],
  eventId: number | string | null | undefined,
  stored: string[] | null | undefined
) => {
  // A ticket with dropdown modifiers (Hotel, Time) mints one token name per
  // permutation. Offer the base name (accepts every permutation at the gate)
  // followed by each exact permutation name.
  const forEvent = (items ?? [])
    .filter((item) => String(item.event_id) === String(eventId ?? ""))
    .flatMap((item) => {
      const groups = parseTicketModifiers(item.modifiers);
      const byName = groups.length ? [item.name, ...permutationTokenNames([item.name], groups)] : [item.name];
      // Then the tokens the ticket actually mints: a multi-day ticket's Day
      // tokens, a bundle's component tokens. The ticket name itself is usually
      // one of them; the Set below drops that repeat.
      return [...byName, ...tokenPayloadNames(item.token_payload)];
    });

  // A box office item can share its name with an online ticket type; list it once.
  const names = Array.from(new Set(forEvent));
  const known = new Set(names);
  const orphans = (stored ?? [])
    .map((name) => String(name ?? "").trim())
    .filter((name) => name && !known.has(name));

  return [
    ...names.map((name) => ({ value: name, label: name })),
    // Marked, so an operator can see which entries no longer match a ticket
    // type and fix them, rather than losing them without noticing.
    ...orphans.map((name) => ({ value: name, label: `${name} (not a current ticket type)` })),
  ];
};
